import type { ITextOptions, Textbox } from "fabric/fabric-impl";
import { TextObjectProperties } from "../types/textTypes";
import { ManageObjectDataFactory, ObjectExtensionFactory, Canvas, Object } from "../types/canvasTypes";
import { OBJECT_MODIFYING_THROTTLE_TIME, OBJECT_TYPE } from "../constants/canvasConstants";
import { createAndAddTextToCanvas, isText } from "../helpers/textHelpers";
import {
  getProcessedCoordinatesAndDimensionsFromFabricObject,
  getProcessedFontSizeFromFabricObject,
  getProcessedCoordinatesFromStoreForCenteredObject,
  modifyActiveObject,
} from "../utils/canvasUtils";
import { throttle } from "@/lib/utils";

const NAME = OBJECT_TYPE.text;
const FONT_SIZE_CHANGE_EVENT = "text:font-size-changed";

export type TextUpdateHandler = (options: Partial<TextObjectProperties>) => void;

type TextExtensionOptions = {
  handleOptionsUpdate?: TextUpdateHandler;
};

export const createTextExtension: ObjectExtensionFactory<
  TextObjectProperties,
  Partial<TextObjectProperties>,
  TextExtensionOptions
> = (options) => {
  const textMovingSubscription = createTextMovingSubscription(options?.handleOptionsUpdate);
  const textRotatingSubscription = createTextRotatingSubscription(options?.handleOptionsUpdate);
  const textResizingSubscription = createTextResizingSubscription(options?.handleOptionsUpdate);
  const textScalingSubscription = createTextScalingSubscription(options?.handleOptionsUpdate);
  const textFontSizeModificationSubscription = createFontSizeModificationSubscription(options?.handleOptionsUpdate);

  return {
    name: NAME,
    onInit: (canvas) => {
      textMovingSubscription.subscribe(canvas);
      textRotatingSubscription.subscribe(canvas);
      textResizingSubscription.subscribe(canvas);
      textScalingSubscription.subscribe(canvas);
      textFontSizeModificationSubscription.subscribe(canvas);
    },
    onDestroy: (canvas) => {
      textMovingSubscription.unsubscribe(canvas);
      textRotatingSubscription.unsubscribe(canvas);
      textResizingSubscription.unsubscribe(canvas);
      textScalingSubscription.unsubscribe(canvas);
      textFontSizeModificationSubscription.unsubscribe(canvas);
    },
    onAddObject: ({ canvas, payload }) => {
      createAndAddTextToCanvas(canvas, payload);
    },
    onModifyObject: ({ canvas, payload }) => {
      // @ts-ignore TODO
      modifyActiveTextObject(canvas, payload);
    },
  };
};

export const createAddTextObjectAction: ManageObjectDataFactory<TextObjectProperties> = (payload) => ({
  name: NAME,
  payload,
});
export const createModifyTextObjectAction: ManageObjectDataFactory<Partial<TextObjectProperties>> = (payload) => ({
  name: NAME,
  payload,
});

type TextMovingHandler = (coordinates: { top: number; left: number }) => void;
function createTextMovingSubscription(handler?: TextMovingHandler) {
  const handleMoving = throttle(({ target }: { target: Object }) => {
    if (!isText(target) || target.left === undefined || target.top === undefined) return;

    const { left, top } = getProcessedCoordinatesAndDimensionsFromFabricObject(target);

    handler?.({
      left,
      top,
    });
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => canvas.on("object:moving", handleMoving as any),
    unsubscribe: (canvas: Canvas) => canvas.off("object:moving", handleMoving as any),
  };
}

type TextRotatingHandler = (options: { angle: number }) => void;
function createTextRotatingSubscription(handler?: TextRotatingHandler) {
  const handleRotating = throttle(({ target }: { target: Object }) => {
    if (!isText(target) || target.angle === undefined) return;

    handler?.({ angle: Math.trunc(target.angle) });
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => canvas.on("object:rotating", handleRotating as any),
    unsubscribe: (canvas: Canvas) => canvas.off("object:rotating", handleRotating as any),
  };
}

type TextResizingHandler = (sizes: { width: number; top: number; left: number }) => void;
function createTextResizingSubscription(handler?: TextResizingHandler) {
  const handleResizing = throttle(({ target }: { target: Object }) => {
    if (!isText(target)) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { height, ...topLeftWidthProps } = getProcessedCoordinatesAndDimensionsFromFabricObject(target);

    handler?.(topLeftWidthProps);
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => canvas.on("object:resizing", handleResizing as any),
    unsubscribe: (canvas: Canvas) => canvas.off("object:resizing", handleResizing as any),
  };
}

type TextScalingHandler = (sizes: { width: number; top: number; left: number; fontSize: number }) => void;
function createTextScalingSubscription(handler?: TextScalingHandler) {
  const handleScaling = throttle(({ target }: { target: Object }) => {
    if (!isText(target)) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { height, ...topLeftWidthProps } = getProcessedCoordinatesAndDimensionsFromFabricObject(target);
    const fontSize = getProcessedFontSizeFromFabricObject(target);

    handler?.({ ...topLeftWidthProps, fontSize });
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => canvas.on("object:scaling", handleScaling as any),
    unsubscribe: (canvas: Canvas) => canvas.off("object:scaling", handleScaling as any),
  };
}

type TextFontSizeModificationHandler = (sizes: { width: number; top: number; left: number }) => void;
function createFontSizeModificationSubscription(handler?: TextFontSizeModificationHandler) {
  const handleModification = throttle(({ target }: { target: Object }) => {
    if (!isText(target)) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { height, ...topLeftWidthProps } = getProcessedCoordinatesAndDimensionsFromFabricObject(target);

    handler?.(topLeftWidthProps);
  }, OBJECT_MODIFYING_THROTTLE_TIME);

  return {
    subscribe: (canvas: Canvas) => canvas.on(FONT_SIZE_CHANGE_EVENT, handleModification as any),
    unsubscribe: (canvas: Canvas) => canvas.off(FONT_SIZE_CHANGE_EVENT, handleModification as any),
  };
}

export function modifyActiveTextObject(canvas: Canvas, options: ITextOptions) {
  const object = canvas?.getActiveObject() as Textbox;

  if (!object) return;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { width, fontSize, ...rest } = options;

  const coordinates = getProcessedCoordinatesFromStoreForCenteredObject({
    top: options.top!,
    left: options.left!,
    width: width!,
    height: object.height! * object.scaleY!,
  });

  const isFontSizeOptionChanged = checkFontSizeChangedAndSetNewValue(object, options.fontSize!);

  // Calculate new scale instead of changing fontSize itself
  const scale = options.fontSize! / object.fontSize!;
  object.set("scaleX", scale);
  object.set("scaleY", scale);

  const calculatedWith = width! / object.scaleX!;

  modifyActiveObject(canvas, {
    ...rest,
    ...coordinates,
    width: calculatedWith,
  });

  // If fontSize option has been changed trigger event to recalculate coordinates and dimensions
  if (isFontSizeOptionChanged) {
    triggerFontSizeChangeEvent(canvas, object);
  }
}

function triggerFontSizeChangeEvent(canvas: Canvas, object: Textbox) {
  canvas.fire(FONT_SIZE_CHANGE_EVENT, { target: object });
}

const previousFontSize = Symbol("previousFontSize");
function checkFontSizeChangedAndSetNewValue(object: Textbox, optionsCurrentFontSize: number) {
  // @ts-ignore
  const previousFontSizeValue = object.get(previousFontSize);
  // @ts-ignore
  object.set(previousFontSize, optionsCurrentFontSize);

  return previousFontSizeValue !== optionsCurrentFontSize;
}
